<script setup lang="ts">
import { computed } from "vue";
import { t } from "@/plugins/i18n";
import FilterRow from "./FilterRow.vue";
import { useRowList } from "./useRowList";
import { parentSummary, soleSelectedKey, type ValueRow } from "./valueRow";

const VIRTUAL_HEIGHT = 320;

const {
	rows,
	isChecked,
	isIndeterminate = () => false,
	canToggle = () => true,
	initialLimit = null,
	flush = false,
	allowBulk = true,
	allowOnly = true,
	searchPlaceholder = null,
	emptyMessage = null,
} = defineProps<{
	rows: ValueRow[];
	isChecked: (row: ValueRow) => boolean;
	isIndeterminate?: (row: ValueRow) => boolean;
	canToggle?: (row: ValueRow) => boolean;

	initialLimit?: number | null;

	flush?: boolean;

	allowBulk?: boolean;
	allowOnly?: boolean;
	searchPlaceholder?: string | null;
	emptyMessage?: string | null;
}>();

const emit = defineEmits<{
	(event: "toggle", row: ValueRow): void;

	(event: "only", row: ValueRow, scope: ValueRow[]): void;
	(event: "select-all", rows: ValueRow[]): void;
	(event: "deselect-all", rows: ValueRow[]): void;

	(event: "drill", row: ValueRow): void;
}>();

const {
	search,
	isFiltered,
	matching,
	visible,
	isBounded,
	shouldVirtualize,
	resolveClick,
	showAll,
} = useRowList({
	rows: () => rows,
	isChecked: (row) => isChecked(row),
	canToggle: (row) => canToggle(row),
	initialLimit: () => initialLimit,
});

const parentText = (row: ValueRow): string | null => {
	const summary = parentSummary(row);

	if (!summary) {
		return null;
	}

	return summary.kind === "path"
		? summary.path
		: t("filterDialog.hierarchy.manyParents", { count: summary.count });
};

const parentTooltip = (row: ValueRow): string | null => {
	const summary = parentSummary(row);

	return summary?.kind === "many" ? summary.paths.join("\n") : null;
};

const soleChecked = computed(() =>
	soleSelectedKey(rows, (row) => isChecked(row))
);

const rowProps = (row: ValueRow) => ({
	label: row.label,
	checked: isChecked(row),
	indeterminate: isIndeterminate(row),
	toggleable: canToggle(row),
	dimmed: !isChecked(row),
	isolated: row.key === soleChecked.value,
	allowOnly,
	canDrill: row.canDrill ?? false,
	parentText: parentText(row),
	parentTooltip: parentTooltip(row),
});

const onToggle = (row: ValueRow, activation: MouseEvent | KeyboardEvent) => {
	const command = resolveClick(row, activation);

	if (command.kind === "select-all") {
		emit("select-all", command.rows);
		return;
	}

	if (command.kind === "deselect-all") {
		emit("deselect-all", command.rows);
		return;
	}

	emit("toggle", command.row);
};

const onOnly = (row: ValueRow) => {
	if (row.key === soleChecked.value) {
		emit("select-all", matching.value);
		return;
	}

	emit("only", row, rows);
};

const placeholder = computed(
	() => searchPlaceholder ?? t("filterDialog.values.search")
);

const nothingMessage = computed(() =>
	isFiltered.value
		? t("filterDialog.rail.empty", { query: search.value })
		: emptyMessage ?? t("filterDialog.values.none")
);

const shownOfTotal = computed(() =>
	t("filterDialog.list.shown", {
		shown: visible.value.length,
		total: rows.length,
	})
);

const showAllLabel = computed(() =>
	t("filterDialog.list.showAll", { total: matching.value.length })
);
</script>
<template>
	<div class="filter-list" :class="{ flush }">
		<div class="pa-2">
			<v-text-field
				v-model="search"
				:placeholder="placeholder"
				prepend-inner-icon="fa: fa-regular fa-magnifying-glass"
				hide-details
				clearable
				autocomplete="off"
			/>
		</div>
		<div class="d-flex align-center ga-1 px-2 pb-2">
			<template v-if="allowBulk">
				<v-btn
					variant="text"
					size="small"
					density="comfortable"
					class="quiet"
					@click="emit('select-all', matching)"
				>
					{{
						isFiltered
							? t("filterDialog.values.selectAllMatching")
							: t("filterDialog.values.selectAll")
					}}
				</v-btn>
				<v-btn
					variant="text"
					size="small"
					density="comfortable"
					class="quiet"
					@click="emit('deselect-all', matching)"
				>
					{{ t("filterDialog.values.deselectAll") }}
				</v-btn>
			</template>
			<span v-if="isFiltered" class="text-caption quiet-text">
				{{ shownOfTotal }}
			</span>
		</div>
		<v-divider />
		<div
			v-if="matching.length === 0"
			class="pa-3 text-body-2 text-center quiet-text"
		>
			{{ nothingMessage }}
		</div>
		<v-virtual-scroll
			v-else-if="shouldVirtualize"
			:items="visible"
			:height="VIRTUAL_HEIGHT"
			item-height="34"
		>
			<template #default="{ item }">
				<filter-row
					v-bind="rowProps(item)"
					@toggle="(activation) => onToggle(item, activation)"
					@only="onOnly(item)"
					@drill="emit('drill', item)"
				/>
			</template>
		</v-virtual-scroll>
		<div v-else class="rows">
			<filter-row
				v-for="row in visible"
				:key="row.key"
				v-bind="rowProps(row)"
				@toggle="(activation) => onToggle(row, activation)"
				@only="onOnly(row)"
				@drill="emit('drill', row)"
			/>
		</div>
		<template v-if="isBounded">
			<v-divider />
			<div class="d-flex align-center ga-2 pa-2">
				<span class="text-caption quiet-text">{{ shownOfTotal }}</span>
				<v-spacer />
				<v-btn variant="tonal" size="small" @click="showAll">
					{{ showAllLabel }}
				</v-btn>
			</div>
		</template>
	</div>
</template>
<style scoped lang="scss">
.filter-list {
	border: 1px solid rgb(var(--color-secondary));
	border-radius: 4px;
	scrollbar-color: rgb(var(--color-secondary)) transparent;

	&.flush {
		border: 0;
		border-radius: 0;
		background: transparent;
	}
}

.rows {
	max-height: 20rem;
	overflow-y: auto;
}

.quiet {
	text-transform: none;
	letter-spacing: 0;
	color: rgb(var(--color-on-background));
}

.quiet-text {
	color: rgb(var(--color-on-background));
}
</style>
